import { useEffect, useState, useMemo } from 'react';
import { Table, Pagination } from 'react-bootstrap';
import {
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  Chip,
  useTheme,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import './snowlog.scss';

const SnowLogListTable = ({
  items = [],
  onEdit,
  onDelete,           // ← NEW prop: callback to trigger delete confirmation
  userRole,           // ← NEW prop: to conditionally show delete button
  scrollThreshold = 992,
}) => {
  const theme = useTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentPage, setCurrentPage] = useState(1);
  const sizePerPage = 10;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [items]);

  const totalPages = Math.ceil(sortedItems.length / sizePerPage);
  const paginatedItems = sortedItems.slice(
    (currentPage - 1) * sizePerPage,
    currentPage * sizePerPage
  );

  // Compute totals for summary row
  const totals = useMemo(() => {
    if (sortedItems.length === 0) return null;

    let totalDurationMinutes = 0;
    let totalAttachments = 0;
    let completedCount = 0;

    sortedItems.forEach(row => {
      if (row.time_started && row.time_completed) {
        const [sh, sm] = row.time_started.split(':').map(Number);
        const [eh, em] = row.time_completed.split(':').map(Number);
        if (!isNaN(sh) && !isNaN(sm) && !isNaN(eh) && !isNaN(em)) {
          const startMin = sh * 60 + sm;
          const endMin = eh * 60 + em;
          const diff = endMin - startMin;
          if (diff > 0) totalDurationMinutes += diff;
        }
      }

      if (Array.isArray(row.attachments)) {
        totalAttachments += row.attachments.length;
      }

      if (row.completed_work?.by) {
        completedCount++;
      }
    });

    return {
      totalLogs: sortedItems.length,
      totalDuration: totalDurationMinutes > 0 ? `${totalDurationMinutes} min` : '—',
      totalAttachments: totalAttachments > 0 ? totalAttachments : '—',
      completedCount,
    };
  }, [sortedItems]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const formatTime = (timeStr) => {
    return timeStr ? timeStr.trim() : 'N/A';
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return '—';
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;
    const diff = endMinutes - startMinutes;
    if (diff <= 0) return '—';
    return `${diff} min`;
  };

  const getTasksSummary = (tasks = {}) => {
    const performed = Object.entries(tasks)
      .filter(([, value]) => value)
      .map(([key]) =>
        key
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );

    return performed.length > 0 ? performed.join(', ') : 'None';
  };

  const getActionsSummary = (actions = []) => {
    if (!Array.isArray(actions) || actions.length === 0) return 'None';
    return actions.join(', ');
  };

  const getAttachmentsCount = (attachments = []) => {
    const count = attachments.length;
    return count === 0 ? 'No attachments' : `${count} attachment${count > 1 ? 's' : ''}`;
  };

  const getPropertyTitle = (property_details) => {
    const property = property_details;
    const propertyInfo = property?.property_id;

    if (!propertyInfo) return 'Unknown Property';

    const title = propertyInfo.title || 'Unknown';
    const address = propertyInfo.address || '';
    const building = property?.building_name || '';

    return (
      <Tooltip
        title={
          <>
            <>{title}</><br />
            {building && <div>{building}</div>}
            {address && <div>{address}</div>}
          </>
        }
        arrow
      >
        <span>
          {title} <br />
          {building && (
            <small style={{ marginLeft: 4 }}>
              ({building})
            </small>
          )}
        </span>
      </Tooltip>
    );
  };

  const getCompletedByInfo = (completedWork) => {
    if (!completedWork?.by) return { text: 'Not completed', color: 'default' };

    const name = completedWork?.completedByName || 'Unknown';
    let roleLabel = '—';

    switch (completedWork.by.role) {
      case 'technical staff':
        roleLabel = 'Staff';
        break;
      case 'vendor':
        roleLabel = 'Vendor';
        break;
      case 'manager':
        roleLabel = 'Manager';
        break;
      default:
        roleLabel = completedWork.by.role;
    }

    return {
      text: `${name} (${roleLabel})`,
      color: 'success',
      tooltip: completedWork.comments ? `Note: ${completedWork.comments}` : undefined,
    };
  };

  const columns = [
    { key: 'date', label: 'Date', sticky: true },
    { key: 'property', label: 'Property', sticky: true },
    { key: 'day', label: 'Day' },
    { key: 'time_started', label: 'Started' },
    { key: 'time_completed', label: 'Completed' },
    { key: 'duration', label: 'Duration' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'actions_performed', label: 'Actions Performed' },
    { key: 'temp_and_conditions', label: 'Weather / Conditions' },
    { key: 'attachments', label: 'Attachments' },
    { key: 'completed_by', label: 'Completed By' },
    { key: 'comments', label: 'Notes' },
    { key: 'actions', label: 'Actions', align: 'center' },
  ];

  const isMobile = windowWidth < scrollThreshold;

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        minHeight: '50vh',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          maxHeight: isMobile ? '65vh' : '75vh',
          overflowY: 'auto',
          overflowX: 'auto',
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: 4,
          },
        }}
      >
        <Table
          hover
          responsive
          size="sm"
          className="snowlog-table"
          sx={{
            '& .MuiTableCell-root': { py: 1.2, px: 2 },
            '& .sticky-first-col': {
              position: 'sticky',
              left: 0,
              backgroundColor: 'background.paper',
              zIndex: 2,
              boxShadow: '2px 0 8px -6px rgba(0,0,0,0.15)',
            },
            '& .summary-row td': {
              fontWeight: 600,
              backgroundColor: theme.palette.grey[100],
              borderTop: `2px solid ${theme.palette.divider}`,
            },
          }}
        >
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={col.sticky ? 'sticky-first-col' : undefined}
                  style={{
                    minWidth:
                      col.key === 'tasks' ? 180 :
                      col.key === 'actions_performed' ? 240 :
                      col.key === 'comments' ? 220 : 100,
                    whiteSpace: 'nowrap',
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="40vh"
                    py={6}
                    color="text.secondary"
                  >
                    <FeatherIcon icon="cloud-snow" size={56} strokeWidth={1.4} style={{ opacity: 0.4 }} />
                    <Typography variant="h6" mt={2} mb={1}>
                      No Snow Removal Logs
                    </Typography>
                    <Typography variant="body2">No records found for this property.</Typography>
                  </Box>
                </td>
              </tr>
            ) : (
              <>
                {paginatedItems.map(row => {
                  const completedInfo = getCompletedByInfo(row.completed_work);

                  return (
                    <tr
                      key={row._id}
                      onClick={() => onEdit(row)}
                      style={{ cursor: 'pointer' }}
                      className={row.completed_work ? 'completed-row' : ''}
                    >
                      {columns.map(col => {
                        let content = '—';

                        switch (col.key) {
                          case 'property':
                            content = getPropertyTitle(row.property_details);
                            break;
                          case 'date':
                            content = formatDate(row.date);
                            break;
                          case 'day':
                            content = row.date
                              ? new Date(row.date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  timeZone: 'UTC'
                                })
                              : '—';
                            break;
                          case 'time_started':
                            content = formatTime(row.time_started);
                            break;
                          case 'time_completed':
                            content = formatTime(row.time_completed);
                            break;
                          case 'duration':
                            content = (
                              <Chip
                                size="small"
                                label={formatDuration(row.time_started, row.time_completed)}
                                color="default"
                                sx={{
                                  minWidth: 68,
                                  '& .MuiChip-label': { fontSize: '0.7rem' },
                                }}
                              />
                            );
                            break;
                          case 'tasks':
                            const tasksStr = getTasksSummary(row.tasks);
                            content = (
                              <Tooltip title={tasksStr} placement="top" arrow>
                                <span className="text-truncate d-block" style={{ maxWidth: 180 }}>
                                  {tasksStr}
                                </span>
                              </Tooltip>
                            );
                            break;
                          case 'actions_performed':
                            const actionsStr = getActionsSummary(row.actions_performed);
                            content = (
                              <Tooltip title={actionsStr} placement="top" arrow>
                                <span className="text-truncate d-block" style={{ maxWidth: 240 }}>
                                  {actionsStr}
                                </span>
                              </Tooltip>
                            );
                            break;
                          case 'temp_and_conditions':
                            content = (
                              <Tooltip title={row.temp_and_conditions || '—'} arrow>
                                <span className="text-truncate d-block" style={{ maxWidth: 160 }}>
                                  {row.temp_and_conditions || '—'}
                                </span>
                              </Tooltip>
                            );
                            break;
                          case 'attachments':
                            const attachmentText = getAttachmentsCount(row.attachments);
                            content = (
                              <Typography
                                variant="body2"
                                fontWeight={row.attachments?.length > 0 ? 500 : 400}
                                color={row.attachments?.length > 0 ? 'success.main' : 'text.secondary'}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {attachmentText}
                              </Typography>
                            );
                            break;
                          case 'completed_by':
                            content = (
                              <Tooltip title={completedInfo.tooltip || ''} arrow>
                                <Chip
                                  size="small"
                                  label={completedInfo.text}
                                  color={completedInfo.color}
                                  sx={{
                                    '& .MuiChip-label': { fontSize: '0.75rem' },
                                  }}
                                />
                              </Tooltip>
                            );
                            break;
                          case 'comments':
                            content = (
                              <Tooltip title={row.comments || '—'} arrow>
                                <span className="text-truncate d-block" style={{ maxWidth: 220 }}>
                                  {row.comments || '—'}
                                </span>
                              </Tooltip>
                            );
                            break;
                          case 'actions':
                            content = (
                              <div className="d-flex gap-2 justify-content-center">
                                {/* Edit button */}
                                <Tooltip title="Edit / View Details" arrow>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEdit(row);
                                    }}
                                    sx={{ minWidth: 40, p: '6px' }}
                                  >
                                    <FeatherIcon icon="edit-2" size={18} />
                                  </Button>
                                </Tooltip>

                                {/* Delete button - only visible to company users */}
                                {userRole === 'company' && (
                                  <Tooltip title="Delete Log" arrow>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(row);
                                      }}
                                      sx={{
                                        minWidth: 40,
                                        p: '6px',
                                        '&:hover': {
                                          backgroundColor: theme.palette.error.light,
                                          color: theme.palette.error.contrastText,
                                        },
                                      }}
                                    >
                                      <FeatherIcon icon="trash-2" size={18} />
                                    </Button>
                                  </Tooltip>
                                )}
                              </div>
                            );
                            break;
                          default:
                            content = row[col.key] ?? '—';
                        }

                        return (
                          <td
                            key={col.key}
                            className={col.sticky ? 'sticky-first-col' : undefined}
                            style={{ verticalAlign: 'middle' }}
                          >
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {/* Summary Row */}
                {totals && (
                  <tr className="summary-row">
                    <td colSpan={2} style={{ fontWeight: 500 }}>
                      Total ({totals.totalLogs} logs)
                    </td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>
                      <Chip
                        size="small"
                        label={totals.totalDuration}
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    </td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>
                      <Typography fontWeight={500}>
                        {totals.totalAttachments}
                      </Typography>
                    </td>
                    <td>
                      <Chip
                        size="small"
                        label={`${totals.completedCount} completed`}
                        color="success"
                        sx={{ fontWeight: 500 }}
                      />
                    </td>
                    <td>—</td>
                    <td>—</td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Box p={2} display="flex" justifyContent="center" bgcolor="background.default">
          <Pagination size="sm">
            <Pagination.Prev
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </Box>
      )}
    </Paper>
  );
};

export { SnowLogListTable };